/**
 * The one-time question about a group layout the member already has a copy of
 * (`docs/plans/group-layout-sync.md`, step 4 — the approved mock).
 *
 * Asked about a layout with **no link** to the group's and the same name: a
 * copy applied by hand before "From your club" recorded links, or an adopted
 * one whose link was lost. Either answer records the link, which is what keeps
 * it from being asked again; a linked copy is never asked about, a republish
 * replaces it silently.
 *
 * **No close button and no Escape.** The mock has two answers and no third, and
 * dismissing would only ask again on the next load. **Replace mine** is the
 * default — focused, and the accent button (owner's decision 1, 2026-10-06).
 *
 * `NewListDialog`'s scrim and panel, so the app has one look for a modal.
 */

import type { LegacyAnswer } from "../lib/group-layouts";

export interface LegacyLayoutDialogProps {
  /** The group as a member reads it — `JAS`. */
  groupName: string;
  layoutName: string;
  onAnswer: (answer: LegacyAnswer) => void;
}

export function LegacyLayoutDialog({ groupName, layoutName, onAnswer }: LegacyLayoutDialogProps) {
  const title = `${groupName} updated "${layoutName}"`;
  return (
    <div style={SCRIM}>
      <div role="dialog" aria-modal="true" aria-label={title} style={PANEL}>
        <h4 style={TITLE}>{title}</h4>
        <p style={BODY}>You already have a layout with that name.</p>
        <div style={FOOT}>
          <button type="button" onClick={() => onAnswer("keep")} style={BTN}>
            Keep mine and add theirs
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => onAnswer("replace")}
            style={{ ...BTN, ...PRIMARY }}
          >
            Replace mine
          </button>
        </div>
      </div>
    </div>
  );
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: 380,
  maxWidth: "calc(100vw - 32px)",
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 13px", fontSize: 13, fontWeight: 600 };

const BODY: React.CSSProperties = { margin: 0, color: "var(--qf-text-muted)" };

const FOOT: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 7,
  marginTop: 16,
};

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};
